import React, { useEffect } from 'react';
import { FormProvider, UploadSingleFile, NextImage, Iconify } from '@sdf/base';
import { Styles } from '@t2c/shared-fe';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { useBrollPage } from '../../../../t2c-fe/hooks/broll/useBrollPage';
import { BrollClipProccessingModal } from '../../broll/BrollClipProccessingModal';
import { Chip, CircularProgress, IconButton, MenuItem, Popover, Select, Switch, Tab, TextField, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { FileUploader } from 'react-drag-drop-files';
import { useSnackbar } from 'notistack';
import { CreditDialog } from '../../create-video/CreditDialog';
import { Tabs } from '@mui/material';
import { AudioTab } from '../../../../t2c-fe/@types';
import { useAudioTabs, usePopover } from '../../../../t2c-fe/hooks';
import { SelectAvatarModal } from '../../talking-face/SelectAvatar';
import { useTalkingFace } from '../../../../t2c-fe/hooks/talking-face/useTalkingFace';
import { TalkingFaceProccessingModal } from '../../talking-face/TalkingFaceProccessingModal';
import { DbModelToDisplayName, TalkingFaceModelType } from 'apps/t2c-fe/utils';

export const TalkingFaceWorkBench = ({ refetchTalkingFaces }) => {
  const {
    handleDataChange,
    handleCreateClick,
    isCreditDialogOpen,
    closeCreditDialog,
    isFetchingRequiredCredits,
    requiredCreditsData,
    handleConfirmGeneration,
    // clipGenerationLoading,
    isProccessingModalOpen,
    closeProccessingModal,
    handleUpgrade,
    isSelectAvatarDialog,
    openSelectAvatarDialog,
    closeSelectAvatarDialog,
    talkingFaceData,
    popoverAnchor,
    handleOpenPopover,
    handleClosePopover,
    handleAvatarSelect,
    handleAudioSelect,
    privateAudioData,
    publicAudioData,
    selectedAvatarImage,
    isLoading,
    newGenerationData,
    isEnhancing,
    handlePromptEnhance,
    isTrain,
    modelName,
    setModelName,
    enableMotion,
    setEnableMotion,
  } = useTalkingFace({ refetchTalkingFaces: refetchTalkingFaces });

  const { enqueueSnackbar } = useSnackbar();
  const maxCharLimit = 3500;

  const { activeTab, handleTabChange, audioList, selectedAudio, handleAudioTrackSelect } =
    useAudioTabs(publicAudioData, privateAudioData);

  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const defaultAudio = publicAudioData?.[0] ?? privateAudioData?.pages?.[0]?.data?.[0];

    if (defaultAudio && !selectedAudio) {
      handleAudioTrackSelect(defaultAudio);
      handleAudioSelect(defaultAudio?.id);
    }
  }, [publicAudioData, privateAudioData]);

  return (
    <Grid container gap={3}>
      <Grid item xs={12}>
        <Typography sx={styles.heading}>
          Type your script here or let our AI generate one for you.{' '}
        </Typography>
        <Box sx={{position: 'relative'}}>
          <TextField
            placeholder="E.g., Write a script for a promotional video, brainstorm blog ideas, or draft an email."
            size="medium"
            type="text"
            name="script"
            multiline
            inputProps={{ maxLength: maxCharLimit }}
            rows={isExtraSmall? 2 : 10}
            sx={styles.textField}
            onChange={(e) => handleDataChange('text', e.target.value?.slice(0, maxCharLimit))}
            value={talkingFaceData.text}
            fullWidth
          />
          <Tooltip title="Enhance prompt with AI" placement="top">
            <span>
              <IconButton
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 4,
                  zIndex: 10,
                }}
                disabled={!talkingFaceData.text.trim() || isEnhancing}
                onClick={handlePromptEnhance}
              >
                {isEnhancing ? 
                  <CircularProgress size={20} /> :
                  <NextImage src={'/assets/icons/create-video_default.svg'} alt="" width={24} height={24} style={{ height: 'auto' }} />
                }
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box
          sx ={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: (theme) => theme.palette.text.placeholder, pl: '2px', pt: '2px' }}
          >
            {talkingFaceData?.text?.length || 0}/{maxCharLimit} characters
          </Typography>
          <Box 
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              mt: 1,
              visibility: modelName === TalkingFaceModelType.HEYGEN ? "visible" : "hidden",
            }}
          >
            <Typography sx={{...styles.label, mt: 0.5}}>Enable Motion</Typography>
            <Switch
              checked={enableMotion}
              onChange={(e) => setEnableMotion(e.target.checked)}
            />
        </Box>
        </Box>
      </Grid>
      <Box display={'flex'} gap={2} width={'100%'} flexDirection={isExtraSmall ? 'column' : 'row'}>
        <Box flex={1}>
          <Typography sx={styles.label}>Enter Name</Typography>
          <TextField
            placeholder="Type name here"
            size="medium"
            type="text"
            name="name"
            sx={styles.textField}
            value={talkingFaceData.name}
            onChange={(e) => handleDataChange('name', e.target.value)}
            fullWidth
          />
        </Box>
        <Box flex={1}>
          <Typography sx={styles.label}>Select Audio </Typography>
          <Box onClick={handleOpenPopover} sx={styles.selectAudioBtn}>
            {!selectedAudio?.name && (
              <Typography sx={{ color: (theme) => theme.palette.text.placeholder }}>
                Select Audio
              </Typography>
            )}
            <Typography> {selectedAudio?.name} </Typography>
            <Iconify icon="mdi:chevron-down" width={16} height={16} />
          </Box>
        </Box>
        <Box flex={1}>
          <Typography sx={styles.label}>Select Model </Typography>
          <Select
            labelId="audio-model-select-label"
            value={modelName}
            fullWidth
            onChange={(e) => setModelName(e.target.value as TalkingFaceModelType)}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 300,
                  background: theme.palette.background.primary,
                  border: `1px solid ${theme.palette.background.border}`,
                },
              },
            }}
            sx={{
              border: `1px solid ${theme.palette.background.border}`,
              background: theme.palette.background.secondary,
              '&:hover': { border: `1px solid ${theme.palette.background.activeBorder}` },
              '.MuiOutlinedInput-notchedOutline': { border: 0 },
              '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                border: 0,
              },
              '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: `1px solid ${theme.palette.background.activeBorder}`,
              },
            }}
          >
            {Object.values(TalkingFaceModelType)
              .filter((output) => output !== TalkingFaceModelType.REMOTION)
              .map((output) => {
                return (
                  <MenuItem key={output} value={output}>
                    {output}
                  </MenuItem>
                )
              })}
          </Select>
        </Box>
      </Box>
      <Grid item xs={12} mt={1.5}>
        <Grid container spacing={2} flexDirection={isExtraSmall ? "column" : "row"}>
          <Grid item xs={12} md={6}>
            <Stack rowGap={2}>
              <Box>
                {selectedAvatarImage && <Typography sx={styles.label}>Selected Avatar</Typography>}

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {selectedAvatarImage && (
                    <Box sx={styles.uploader}>
                      <NextImage src={selectedAvatarImage} height={100} width={100} alt="" />
                    </Box>
                  )}

                  <Box sx={styles.avatarSelectBtn} onClick={openSelectAvatarDialog}>
                    <NextImage src={'/assets/icons/Avatars.svg'} alt="" width={49} height={49} style={{ height: 'auto' }} />
                    <Typography fontWeight={500}>
                      {talkingFaceData.avatarId ? 'Choose other avatar' : 'Choose avatar'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Stack>
          </Grid>
          <Grid
            item
            xs={6}
            sx={{
              alignContent: 'flex-end',
            }}
          >
            <Button
              variant="contained"
              fullWidth
              sx={styles.createBtn}
              endIcon={<NextImage src={'/assets/icons/Ai.svg'} alt="" width={24} height={24} />}
              onClick={handleCreateClick}
              disabled={isFetchingRequiredCredits || isLoading}
            >
              Create
            </Button>
          </Grid>
        </Grid>
      </Grid>
      {/* <BrollClipProccessingModal data={} /> */}

      <CreditDialog
        isOpen={isCreditDialogOpen}
        creditDetail={{
          availableCredit: requiredCreditsData?.availableCredits,
          creditNeeded: requiredCreditsData?.requiredCredits,
          creditsInUse: requiredCreditsData?.creditsInUse,
        }}
        onClose={closeCreditDialog}
        onConfirm={handleConfirmGeneration}
        isFetchingCreditDetail={isFetchingRequiredCredits}
        onUpgrade={handleUpgrade}
        isLoading={isLoading}
        enoughCreditConfirmBtnTitle={'Generate'}
        enoughCreditModalTitle="Confirm Generation"
        upgradeButtonTitle="Upgrade"
        isTrain={isTrain}
      />

      {isProccessingModalOpen && (
        <TalkingFaceProccessingModal
          handleCloseDialog={closeProccessingModal}
          shouldOpenDialog={isProccessingModalOpen}
          data={newGenerationData}
        />
      )}

      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={styles.audioPopover}
      >
        <Box sx={styles.popoverContainer}>
          <Box sx={styles.popoverTabs}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="audio tabs">
              <Tab label="Private Audios" value={AudioTab.Private} />
              <Tab label="Public Audios" value={AudioTab.Public} />
            </Tabs>
          </Box>
          <Box sx={styles.popoverContent}>
            {!!audioList?.length &&
              audioList?.map((audio) => (
                <Box
                  key={audio?.id}
                  sx={{
                    ...styles.audioItem,
                    backgroundColor:
                      talkingFaceData.audioId === audio?.id
                        ? (theme) => theme.palette.background.ternary
                        : (theme) => theme.palette.background.secondary,
                    '&:hover': {
                      backgroundColor:
                        talkingFaceData.audioId === audio?.id
                          ? (theme) => theme.palette.background.ternary
                          : (theme) => theme.palette.background.ternary,
                    },
                  }}
                  onClick={() => {
                    handleAudioTrackSelect(audio);
                    handleAudioSelect(audio?.id);
                    handleClosePopover();
                  }}
                >
                  <Typography
                    key={audio?.id}
                  >
                    {audio?.name}
                  </Typography>
                  <Stack sx={{ marginTop: 1, gap: 1, flexDirection: 'row', flexWrap: 'wrap' }}>
                    {audio?.modelName && (
                      <Chip
                        label={DbModelToDisplayName[audio.modelName as keyof typeof DbModelToDisplayName] || audio.modelName}
                        variant="outlined"
                        sx={styles.labelChips}
                      />
                    )}
                    {audio.userAudioSample?.flatMap((sample) =>
                      sample?.tags?.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag.value}
                          variant="outlined"
                          sx={styles.labelChips}
                        />
                      ))
                    )}
                  </Stack>
                </Box>
              ))}
          </Box>
        </Box>
      </Popover>
      {isSelectAvatarDialog && (
        <SelectAvatarModal
          shouldOpenDialog={isSelectAvatarDialog}
          handleCloseDialog={closeSelectAvatarDialog}
          handleOnClick={handleAvatarSelect}
        />
      )}
    </Grid>
  );
};

const styles: Styles = {
  textField: {
    '& .MuiOutlinedInput-root': {
      border: '1px solid',
      borderRadius: '6px',
      borderColor: (theme) => theme.palette.background.ternary,
      backgroundColor: (theme) => theme.palette.background.secondary,
      '&:hover': {
        borderColor: (theme) => theme.palette.background.activeBorder,
      },
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: (theme) => theme.palette.text.placeholder,
    },
  },
  heading: {
    fontSize: 14,
    fontWeight: 500,
    color: (theme) => theme.palette.text.primary,
    marginBottom: '5px',
    marginTop: '5px',
  },
  label: {
    fontSize: 14,
    fontWeight: 400,
    color: (theme) => theme.palette.text.primary,
    marginBottom: '5px',
  },
  uploadContainer: {
    height: 100,
    width: 120,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    marginTop: '10px',
  },
  createBtn: {
    py: 3,
    fontSize: 16,
    color: (theme) => theme.palette.text.primary,
    boxShadow: 'none',
  },
  uploading: {
    position: 'relative',
    borderRadius: '10px',
    width: '210px',
  },
  uploader: {
    mr: 3,
    height: 70,
    width: 70,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    textAlign: 'center',
    borderRadius: '6px',
    flexDirection: 'column',
    borderColor: (theme) => theme.palette.background.border,
    backgroundColor: (theme) => theme.palette.background.primary,
    cursor: 'pointer',
  },
  selectAudioBtn: {
    cursor: 'pointer',
    border: '1px solid',
    borderColor: (theme) => theme.palette.background.border,
    paddingX: 1,
    height: 52,
    borderRadius: '6px',
    // minWidth: 125,
    // maxWidth: 200,
    '&:hover': {
      borderColor: (theme) => theme.palette.background.activeBorder,
    },
    backgroundColor: (theme) => theme.palette.background.secondary,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    // columnGap: 2,
  },
  popoverContainer: {
    width: '100%',
    paddingX: 2,
    paddingBottom: 2,
    border: '1px solid',
    borderColor: (theme) => theme.palette.background.border,
    backgroundColor: (theme) => theme.palette.background.primary,
  },
  popoverTabs: {
    borderBottom: 1,
    borderColor: 'divider',
    p: 1,
  },
  popoverContent: {
    maxHeight: 200, // Adjust height for scroll
    overflowY: 'auto',
    mt: 2,
    overflowX: 'hidden',
    p: 1,
  },
  audioItem: {
    padding: 1,
    width: '100%',
    cursor: 'pointer',
    my: 0.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  audioPopover: {
    minWidth: 300,
  },
  avatarSelectBtn: {
    p: '10px',
    display: 'flex',
    gap: 1,
    borderWidth: 1,
    borderRadius: 56,
    borderColor: (theme) => theme.palette.background.border,
    cursor: 'pointer',
  },
  labelChips: {
    backgroundColor: (theme) => theme.palette.background.ternary,
    fontSize: '12px',
    paddingX: '2px',
    height: '20px',
    borderColor: '#b9abab',
    borderRadius: '5px',
    color: '#b9abab'
  },
};
